import React, { useEffect, useRef, useState } from 'react';
import { Seo } from './components/Seo';
import { MetaPixel } from './components/MetaPixel';
import { BundleMockup, BonusCover } from './components/BundleMockup';
import { scrollToOffer, goToCheckout } from './lib/checkout';
import { PRICE, ORIGINAL_PRICE, BONUS_IMAGES, SUPPORT_EMAIL, INSTAGRAM_HANDLE, INSTAGRAM_URL } from './data/config';
import { VOLUMES, PAINS, INSIDE, PREVIEW, BONUSES, STEPS, FAQ, Volume } from './data/content';
const BONUS_TOTAL = BONUSES.reduce((s, b) => s + b.value, 0);
const TOTAL_VALUE = ORIGINAL_PRICE + BONUS_TOTAL;
function Label({
  children,
  light = false
}: {
  children: React.ReactNode;
  light?: boolean;
}) {
  return <p className={`text-[11px] font-bold uppercase tracking-[0.22em] ${light ? 'text-terra' : 'text-sage'}`} data-id="element-0" data-mp="2wjgqlh49rtfw">{children}</p>;
}

// Scroll-only button (never goes to checkout).
function SeeButton({
  text
}: {
  text: string;
}) {
  return <button type="button" onClick={scrollToOffer} className="w-full rounded-full bg-terra px-5 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_6px_0_#b85a42] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_3px_0_#b85a42] sm:text-xl" data-id="element-1" data-mp="23ujlygszdv5m">
      {text} ↓
    </button>;
}
function VolumeItem({
  v,
  open,
  onToggle
}: {
  v: Volume;
  open: boolean;
  onToggle: () => void;
}) {
  const start = parseInt(v.range.replace(/[^0-9–]/g, '').split('–')[0], 10);
  return <div className="card overflow-hidden" data-id="element-2" data-mp="2zmo6bandcp36">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 p-4 text-left" aria-expanded={open} data-id="element-3" data-mp="33moqzd129wio">
        <span className="font-display flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sage text-xl font-extrabold text-cream" data-id="element-4" data-mp="38li6tc3tlzm0">{v.num}</span>
        <span className="min-w-0 flex-1" data-id="element-5" data-mp="3dii5v329623m">
          <span className="block text-[10px] font-bold tracking-[0.15em] text-muted" data-id="element-6" data-mp="nuhg0bzipzr4">VOLUME {v.num} · {v.range.toUpperCase()}</span>
          <span className="font-display block text-lg font-bold leading-tight sm:text-xl" data-id="element-7" data-mp="sssr0q1jugax">{v.title}</span>
          <span className="block text-xs italic text-ink/70" data-id="element-8" data-mp="38kwyktgo3k7">“{v.pack}”</span>
        </span>
        <span className={`shrink-0 text-2xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-9" data-mp="3ucjcjtti8btq">+</span>
      </button>
      {open && <div className="border-t border-line bg-creamy/60 p-4" data-id="element-10" data-mp="85ul7x54696g">
          <p className="text-sm text-ink/85" data-id="element-11" data-mp="83i23gnmdsrt">{v.summary}</p>
          <ol className="mt-3 grid gap-x-4 gap-y-1.5 sm:grid-cols-2" data-id="element-12" data-mp="3gmfvdh9hf8b5">
            {v.sessions.map((s, i) => <li key={s} className="flex min-w-0 gap-2 text-sm" data-id="element-13" data-mp="3bncnzq614q00" data-mpi={i}>
                <span className="shrink-0 text-[12px] font-bold text-terra" data-id="element-14" data-mp="3qaxs9nen4v9i">{String(start + i).padStart(2, '0')}</span>
                <span className="min-w-0 break-words" data-id="element-15" data-mp="3lk7m47ye4odr">{s}</span>
              </li>)}
          </ol>
          <p className="mt-3 text-xs font-semibold text-sage" data-id="element-16" data-mp="2xuddy3p0nnzu">+ 13 matching client worksheets</p>
        </div>}
    </div>;
}
function FaqItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-line" data-id="element-17" data-mp="18c6p87f6dygh">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start justify-between gap-3 py-4 text-left" aria-expanded={open} data-id="element-18" data-mp="1d4hqpepkdsb5">
        <span className="font-semibold" data-id="element-19" data-mp="yh6erbph06ai">{q}</span>
        <span className={`shrink-0 text-xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-20" data-mp="13eew5a2so6ap">+</span>
      </button>
      {open && <p className="pb-4 text-sm leading-relaxed text-ink/85" data-id="element-21" data-mp="1r4sfmc7bcwto">{a}</p>}
    </div>;
}

// Mobile-only sticky bar: scrolls to #offer, hidden near the top and while the offer is visible.
function StickyBar() {
  const [show, setShow] = useState(false);
  const offerVisible = useRef(false);
  const scrolled = useRef(false);
  useEffect(() => {
    const update = () => setShow(scrolled.current && !offerVisible.current);
    const onScroll = () => {
      scrolled.current = window.scrollY > 520;
      update();
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    const el = document.getElementById('offer');
    let io: IntersectionObserver | null = null;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(entries => {
        offerVisible.current = entries[0].isIntersecting;
        update();
      }, {
        threshold: 0.05
      });
      io.observe(el);
    }
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      io?.disconnect();
    };
  }, []);
  return <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-3 py-2 backdrop-blur transition-transform duration-300 md:hidden ${show ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-22" data-mp="14wnbqwme0zst">
      
    </div>;
}
export function App() {
  const [openVol, setOpenVol] = useState<number>(0);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#fffdf9]" data-id="element-23" data-mp="3kob2ubepzy3p">
      <Seo data-id="element-24" data-mp="3gmpjc77oklkj" />
      <MetaPixel data-id="element-25" data-mp="3glxxho3vy27g" />

      <header className="border-b border-line bg-white" data-id="element-26" data-mp="3glel0mx0860t">
        
      </header>

      {/* 1. HERO */}
      <section className="bg-sagegrad text-white" data-id="element-27" data-mp="1w5q4cfjjm5ah">
        <div className="mx-auto grid max-w-2xl items-center gap-2 px-4 py-10 md:py-16" style={{
        paddingTop: "14px",
        paddingBottom: "24px"
      }} data-id="element-28" data-mp="2060cnyut57si">
          <div className="min-w-0" data-id="element-29" data-mp="254bk62xmr67k">
            <span className="pill bg-white/15 text-cream" data-id="element-30" data-mp="2530kofb7wkm9">FOR LIFE, CAREER, EXECUTIVE & WELLNESS COACHES</span>
            <h1 className="font-display mt-4 text-[40px] font-extrabold leading-[1.02] sm:text-6xl" data-id="element-31" data-mp="2euwswj70pr47">
              Stop planning sessions from scratch.
              <br data-id="element-32" data-mp="2jsoi35ga2z4p" />
              <span className="text-terra" data-id="element-33" data-mp="2js6gdtaujspe">Start coaching.</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/90 sm:text-lg" data-id="element-34" data-mp="2nww7l4uk8j3z">
              <strong className="text-white" data-id="element-35" data-mp="2stmr625kpwu5">78 ready-to-run coaching sessions</strong>, each with its own <strong className="text-white" data-id="element-36" data-mp="tid9rfkyr3sa">client worksheet</strong>: goals, mindset, stress,
              habits, boundaries, leadership and career. Open it, pick a session, and coach.
            </p>
            <div className="mt-5" data-id="element-37" data-mp="21slrnuxfiurw">
              <BundleMockup data-id="element-38" data-mp="21ru4n8hwe5d8" />
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-white/90" data-id="element-39" data-mp="1wt0qdduseac0">
              <li data-id="element-40" data-mp="1wuu237k9bkqv">✔ 6 volumes · 156 sessions & worksheets</li>
              <li data-id="element-41" data-mp="2at1wp8ttm9yv">✔ For 1:1, groups and online sessions</li>
              <li data-id="element-42" data-mp="25snvoaqn00ac">✔ + 4 bonuses included free</li>
            </ul>
          </div>
          <div className="min-w-0" data-id="element-43" data-mp="2kqgaf0od0knh">
            <div className="mt-4" data-id="element-44" data-mp="2kpelbsaidxmr">
              <SeeButton text="See the complete system now" data-id="element-45" data-mp="2kolpvnh02ujx" />
            </div>
            <p className="mt-3 text-center text-[12px] text-white/75" data-id="element-46" data-mp="2fqk58y3xen6c">Instant PDF access · 7-day risk-free guarantee</p>
          </div>
        </div>
      </section>

      {/* 2. SOUND FAMILIAR */}
      <section className="bg-creamy" data-id="element-47" data-mp="2pig265ua078z">
        <div className="mx-auto max-w-5xl px-4 py-14" style={{
        paddingTop: "25px",
        paddingBottom: "19px"
      }} data-id="element-48" data-mp="2pjiv0jb5sjrj">
          <Label data-id="element-49" data-mp="2yhuuzbxx0twn">Sound familiar?</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-50" data-mp="2yiw43avdf6bq">You love coaching. Not rebuilding sessions every week.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-51" data-mp="3n2prbgz49n11">
            {PAINS.map(p => <div key={p.title} className="card min-w-0 p-5" data-id="element-52" data-mp="3n0dcvdskhusq">
                <span className="pill bg-sage/10 text-sage" data-id="element-53" data-mp="384nm72cfstho">{p.tag}</span>
                <h3 className="font-display mt-3 text-xl font-bold leading-tight" data-id="element-54" data-mp="3d3qt6sbz99sa">{p.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-55" data-mp="2zclrlkp1u7fb">{p.text}</p>
              </div>)}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-lg" data-id="element-56" data-mp="3443onxnybnv0">
            You already have the skills. This gives you the sessions, written and ready, so your prep time goes back to your clients and your life.
          </p>
        </div>
      </section>

      {/* 3. VOLUMES */}
      <section className="bg-white" data-id="element-57" data-mp="2gej274sla8gz">
        <div className="mx-auto max-w-3xl px-4 py-14" style={{
        paddingTop: "18px",
        paddingBottom: "22px"
      }} data-id="element-58" data-mp="2gfkfzh0zp92e">
          <Label data-id="element-59" data-mp="2kfcq3y617n9v">6 volumes · 78 sessions · 78 worksheets</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-60" data-mp="30vnb80g7vcbv">A session ready for whatever your client brings.</h2>
          <p className="mt-3 text-ink/80" data-id="element-61" data-mp="2vu7q68e1h5th">Open each volume to see all 13 session titles.</p>
          <div className="mt-8 space-y-4" data-id="element-62" data-mp="2qvcyegy3g6x8">
            {VOLUMES.map((v, i) => <VolumeItem key={v.num} v={v} open={openVol === i} onToggle={() => setOpenVol(openVol === i ? -1 : i)} data-id="element-63" data-mp="2lx31o1wq0y9i" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* 4. INSIDE EVERY SESSION + PREVIEW */}
      <section className="bg-sagegrad text-white" data-id="element-64" data-mp="2d514qdlv203z">
        <div className="mx-auto max-w-5xl px-4 py-14" style={{
        paddingTop: "15px",
        paddingBottom: "20px"
      }} data-id="element-65" data-mp="286pva3zh9rma">
          <Label light data-id="element-66" data-mp="237v9qua4loyh">Inside every session</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-67" data-mp="239g7stvnfq13">Everything you need to run it, on one page.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-68" data-mp="1u76yg7c4a2pa">
            {INSIDE.map((p, i) => <div key={p.title} className="min-w-0 rounded-2xl border border-white/20 bg-white/10 p-5" data-id="element-69" data-mp="9p66u6rberve" data-mpi={i}>
                <p className="text-[11px] font-bold text-terra" data-id="element-70" data-mp="dq7zg4qq85oe">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-display mt-1 text-xl font-bold" data-id="element-71" data-mp="ipucad2n7jwo">{p.title}</h3>
                <p className="mt-1 text-sm text-white/85" data-id="element-72" data-mp="nonntzydd0u7">{p.text}</p>
              </div>)}
          </div>

          <div className="mt-10 min-w-0 rounded-2xl bg-white p-5 text-ink sm:p-7" data-id="element-73" data-mp="3m6b1nuz2l25c">
            <span className="pill bg-terra/15 text-terra" data-id="element-74" data-mp="3m4gh4hrpnc1s">REAL SESSION PREVIEW</span>
            <h3 className="font-display mt-3 text-2xl font-extrabold leading-tight text-sage" data-id="element-75" data-mp="3r5ntyv8pn1fb">{PREVIEW.title}</h3>
            <p className="mt-3 text-sm" data-id="element-76" data-mp="3w2nvcsum7g03"><strong data-id="element-77" data-mp="4qbf2fbddsz5">Goal:</strong> {PREVIEW.goal}</p>
            <p className="mt-1 text-sm" data-id="element-78" data-mp="4r4d8rw9ja5o"><strong data-id="element-79" data-mp="33ftd82l9ssps">Best for:</strong> {PREVIEW.bestFor}</p>
            <p className="mt-1 text-sm text-muted" data-id="element-80" data-mp="33fa6zj4zfsrl">{PREVIEW.time}</p>
            <p className="mt-4 text-sm font-bold" data-id="element-81" data-mp="38e4ngefh958j">Core exercise</p>
            <ol className="mt-2 space-y-1.5" data-id="element-82" data-mp="2f759n07a05e5">
              {PREVIEW.steps.map((s, i) => <li key={s} className="flex min-w-0 gap-2 text-sm" data-id="element-83" data-mp="2f97w5a97zge4" data-mpi={i}>
                  <span className="shrink-0 font-bold text-terra" data-id="element-84" data-mp="2k4wz7nkg7ff6">{i + 1}.</span>
                  <span className="min-w-0 break-words" data-id="element-85" data-mp="2p51gvgx3eg6z">{s}</span>
                </li>)}
            </ol>
            <p className="mt-4 text-sm font-bold" data-id="element-86" data-mp="2u4nwty4b2ajh">Powerful questions</p>
            <ul className="mt-2 space-y-1" data-id="element-87" data-mp="1wgnfcr6sp1qo">
              {PREVIEW.questions.map(q => <li key={q} className="text-sm italic text-ink/85" data-id="element-88" data-mp="1wej57riaz6hy">• {q}</li>)}
            </ul>
            <p className="mt-4 rounded-xl bg-creamy p-3 text-xs text-ink/80" data-id="element-89" data-mp="21g03df9ekeuu">…plus the group and online version, the between-session action, the closing question, a coach note and Worksheet 02 for your client.</p>
          </div>
        </div>
      </section>

      {/* 5. BONUSES */}
      <section className="bg-creamy" data-id="element-90" data-mp="1dpcqsgojl11y">
        <div className="mx-auto max-w-5xl px-4 py-14" style={{
        paddingTop: "18px",
        paddingBottom: "24px"
      }} data-id="element-91" data-mp="1dobey9zpcaj6">
          <Label data-id="element-92" data-mp="3vz8q2674nef0">Included free · 4 bonuses</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-93" data-mp="3vyyy4d2iux6o">Plus 4 bonuses for your coaching practice.</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-94" data-mp="3myu372eilr9e">
            {BONUSES.map((b, i) => <div key={b.title} className="card flex min-w-0 gap-4 p-4" data-id="element-95" data-mp="3n0x1satqgspq" data-mpi={i}>
                <div className="w-[38%] max-w-[150px] shrink-0" data-id="element-96" data-mp="3hz7p6qqv46zj">
                  <BonusCover index={i} image={BONUS_IMAGES[i] || ''} short={b.short} data-id="element-97" data-mp="3d0wfqh4hbyhu" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-98" data-mp="388v7r7ecgtxm">
                  <p className="text-[11px] font-bold text-terra" data-id="element-99" data-mp="3883als53mo58">BONUS {i + 1}</p>
                  <h3 className="font-display text-lg font-bold leading-tight sm:text-xl" data-id="element-100" data-mp="348kpav9d8b1v">{b.title}</h3>
                  <p className="mt-1 text-sm text-ink/80" data-id="element-101" data-mp="2z754n4b3o6k0">{b.text}</p>
                  <p className="mt-2 text-sm" data-id="element-102" data-mp="2u8tv6uopvy2b">
                    <span className="text-muted line-through" data-id="element-103" data-mp="2pb2422vwcfz6">${b.value}</span> <span className="font-bold text-go" data-id="element-104" data-mp="2pbln5mxa9aes">FREE</span>
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section className="bg-white" data-id="element-105" data-mp="2ko42kcpeyzxm">
        <div className="mx-auto max-w-5xl px-4 py-14" style={{
        paddingTop: "24px"
      }} data-id="element-106" data-mp="26mrmm1a96ybq">
          <Label data-id="element-107" data-mp="26p4dj3xzoj0x">How it works</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-108" data-mp="2antqivf47yk1">Open it. Pick a session. Coach.</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-109" data-mp="2yf8wqza0j00k">
            {STEPS.map(s => <div key={s.n} className="card min-w-0 p-5" data-id="element-110" data-mp="33dad3cg20l8c">
                <span className="font-display text-5xl font-extrabold leading-none text-terra" data-id="element-111" data-mp="33ev6uzujo055">{s.n}</span>
                <h3 className="font-display mt-3 text-xl font-bold" data-id="element-112" data-mp="2tn7k3c8xn7a0">{s.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-113" data-mp="2tl4p0dsxanyf">{s.text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. OFFER — the ONLY checkout button lives here */}
      <section id="offer" className="bg-sagegrad scroll-mt-4 text-white" data-id="element-114" data-mp="3m5moeoooqwum">
        <div className="mx-auto max-w-xl px-4 py-14" style={{
        paddingBottom: "21px"
      }} data-id="element-115" data-mp="1an8b4iqidf0b">
          <div className="text-center" data-id="element-116" data-mp="15of0qz6iq44d">
            <Label light data-id="element-117" data-mp="15ndjgcywsle7">The offer</Label>
            <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-118" data-mp="1kem5yd7ilqn4">Everything you get</h2>
          </div>
          <div className="mt-6 overflow-hidden rounded-2xl bg-white text-ink shadow-2xl" data-id="element-119" data-mp="1ffrpvj0xmg6p">
            <div className="p-4 sm:p-6" data-id="element-120" data-mp="rqgoq0fo6js1">
              <ul className="divide-y divide-line text-sm" data-id="element-121" data-mp="mqlv3p4ifg3a">
                <li className="flex items-center justify-between gap-3 py-3" data-id="element-122" data-mp="ms5cxk5tdz76">
                  <span className="min-w-0 font-semibold" data-id="element-123" data-mp="11owbxsbkt8w9">📘 150+ Done-For-You Coaching Sessions & Worksheets™ · 6 Volumes</span>
                  <span className="shrink-0 text-muted line-through" data-id="element-124" data-mp="wngt86wvfeh1">${ORIGINAL_PRICE}</span>
                </li>
                {BONUSES.map(b => <li key={b.title} className="flex items-center justify-between gap-3 py-3" data-id="element-125" data-mp="283xb06vwe772">
                    <span className="flex min-w-0 items-start gap-2" data-id="element-126" data-mp="hmtf5etyb6c6">
                      <span className="shrink-0 text-base leading-none" data-id="element-127" data-mp="honzaqxeeuf7">🎁</span>
                      <span className="min-w-0" data-id="element-128" data-mp="mmpjxgah31ss">{b.title}</span>
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-right" data-id="element-129" data-mp="qmgfdxay263n">
                      <span className="text-muted line-through" data-id="element-130" data-mp="vn48qpmnaz11">${b.value}</span> <span className="font-bold text-go" data-id="element-131" data-mp="vki0enrxkfhr">FREE</span>
                    </span>
                  </li>)}
                <li className="flex items-center gap-2 py-3" data-id="element-132" data-mp="15kscu6655ivv">
                  <span className="shrink-0 font-bold text-go" data-id="element-133" data-mp="15j7gcb09npvd">✔</span>
                  <span className="font-semibold" data-id="element-134" data-mp="1acjutsb8egae">7-Day Risk-Free Guarantee</span>
                </li>
                <li className="flex items-center gap-2 py-3" data-id="element-135" data-mp="1ec10alqnrdfk">
                  <span className="shrink-0 font-bold text-go" data-id="element-136" data-mp="1jc70iqazu899">✔</span>
                  <span className="font-semibold" data-id="element-137" data-mp="1je02vtekq6bg">Lifetime Access &amp; Free Updates</span>
                </li>
              </ul>
              <div className="mt-4 border-t border-line pt-4 text-center" data-id="element-138" data-mp="1yf6wh3gdy6xi">
                <p className="text-sm" data-id="element-139" data-mp="1yd40m2sjxjkv">
                  Total value: <span className="text-muted line-through" data-id="element-140" data-mp="1tf2hxiz1fm9x">${TOTAL_VALUE}</span>
                </p>
                <p className="mt-1 text-sm" data-id="element-141" data-mp="1oi2bv826ujit">
                  Regular price <span className="line-through" data-id="element-142" data-mp="1kfffquyhicft">${ORIGINAL_PRICE}</span> · your price, one-time payment:
                </p>
                <p className="font-display text-7xl font-extrabold leading-none text-sage" data-id="element-143" data-mp="2h67zq3ojgomv">${PRICE}</p>
                <p className="mt-3 text-xs text-muted" data-id="element-144" data-mp="2c6br1r4wc90z">· Payment will be processed in your local currency ·</p>
                <button type="button" onClick={goToCheckout} className="mt-3 w-full rounded-full bg-go px-4 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_5px_0_#248a16] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_2px_0_#248a16] sm:text-xl" data-id="element-145" data-mp="27ets18mfoiin">
                  Get the sessions now
                </button>
                <p className="mt-3 text-[11px] text-muted" data-id="element-146" data-mp="t7xbul0ypvo3">Secure checkout · Instant PDF access</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. GUARANTEE */}
      <section className="bg-creamy" data-id="element-147" data-mp="jidsle9c7zsp">
        <div className="mx-auto max-w-3xl px-4 py-14" style={{
        paddingTop: "15px",
        paddingBottom: "24px"
      }} data-id="element-148" data-mp="jhcjvgfsnpe5">
          <div className="card flex flex-col items-center gap-5 p-6 text-center sm:flex-row sm:text-left" data-id="element-149" data-mp="ofmffs5fks0a">
            
            <div className="min-w-0" data-id="element-150" data-mp="ajip49xoh3t3">
              <h2 className="font-display text-3xl font-extrabold leading-tight" data-id="element-151" data-mp="fh22z8qgreki">7-Day Risk-Free Guarantee</h2>
              <p className="mt-2 text-ink/85" data-id="element-152" data-mp="k1gbjn4q9sw">
                Open the volumes and run a session with your next client. If the toolkit does not fit your coaching, email us within 7 days of purchase and you get your
                money back.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-white" data-id="element-153" data-mp="1zu9o866dgof5">
        <div className="mx-auto max-w-3xl px-4 py-14" style={{
        paddingTop: "17px",
        paddingBottom: "28px"
      }} data-id="element-154" data-mp="3kdvadwdhni6w">
          <Label data-id="element-155" data-mp="3fg39wdys2nr3">Questions</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-156" data-mp="3fgwg9dttrb8p">Frequently asked questions</h2>
          <div className="mt-6 border-t border-line" data-id="element-157" data-mp="3u613xaje27o7">
            {FAQ.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-158" data-mp="3pbwvsnydztlj" />)}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA (scrolls to the offer) */}
      <section className="bg-sagegrad text-white" data-id="element-159" data-mp="2y6ppwo0rbzb">
        <div className="mx-auto max-w-2xl px-4 py-14 text-center" style={{
        paddingTop: "17px",
        paddingBottom: "27px"
      }} data-id="element-160" data-mp="2xdeoji120bd">
          <h2 className="font-display text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-161" data-mp="gvlh36xu1ttx">
            Less time planning.
            <br data-id="element-162" data-mp="2ioswshhcydgc" />
            <span className="text-terra" data-id="element-163" data-mp="2io9ev0rpjp2b">More time coaching.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-white/85" data-id="element-164" data-mp="2dq7v0dmgjlps">78 sessions, 78 worksheets and 4 bonuses, ready for your next client, group or workshop.</p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-165" data-mp="1rw98jffw9sej">
            <SeeButton text="See the complete system now" data-id="element-166" data-mp="1wvltfun7gpeh" />
          </div>
        </div>
      </section>

      {/* 11. FOOTER */}
      <footer className="border-t border-line bg-white pb-24 md:pb-10" style={{
      paddingBottom: "43px"
    }} data-id="element-167" data-mp="21nn7nm3xoq75">
        <div className="mx-auto max-w-3xl px-4 pt-10 text-center" style={{
        paddingTop: "13px",
        paddingBottom: "13px"
      }} data-id="element-168" data-mp="26kdls3w6hbpx">
          <p className="font-display text-2xl font-extrabold tracking-wide text-sage" data-id="element-169" data-mp="190at9z7xfoox">
            SIGMA SMART<span className="text-terra" data-id="element-170" data-mp="1dyugs42658je">™</span>
          </p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-171" data-mp="1ivb4ix5ghkvy">
            <BundleMockup compact data-id="element-172" data-mp="1nv7a30tsxkdm" />
          </div>
          <p className="mt-6 text-sm" data-id="element-173" data-mp="1nvystb2avnmh">
            📧{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="break-all font-semibold underline" data-id="element-174" data-mp="2ubj7gzosuy85">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p className="mt-2 text-sm" data-id="element-175" data-mp="2z8j5qofeqwop">
            📲{' '}
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline" data-id="element-176" data-mp="nna9ywhs243m">
              {INSTAGRAM_HANDLE}
            </a>
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-xs leading-relaxed text-muted" data-id="element-177" data-mp="dwxpt5dvkcy0">
            Disclaimer: this is a digital toolkit of coaching session guides and worksheets for professional coaches, for educational purposes. It is non-clinical and is
            not therapy, counseling, medical or psychological advice. It does not guarantee any result for you or your clients. Refer clients who need mental health
            support to a licensed professional.
          </p>
          <p className="mt-4 text-[11px] text-muted" data-id="element-178" data-mp="3r5kao6zriuqm">© {new Date().getFullYear()} SIGMA SMART™ · All rights reserved.</p>
        </div>
      </footer>

      {/* 12. MOBILE STICKY BAR (scrolls to the offer only) */}
      <StickyBar data-id="element-179" data-mp="1kd9p59sfo0v6" />
    </div>;
}